import * as React from 'react';
import { FormAnswer, FormDefinition, FormElement, FormMediaRef, FormOption } from '../../types/formSchema';
import { FormAnswerMap, FormErrorMap, isElementVisible } from '../../helpers/form/formRuntime';
import { isFormElementRequired, validateFormDefinition } from '../../helpers/form/formRuntime';
import {
  FORM_START_PAGE_ID,
  buildFormPages,
  hasFormFlow,
  resolveNextPageAction
} from '../../helpers/form/formFlowRuntime';
import {
  buildFormMediaUrl,
  normalizeFormMediaUrl,
  resolveLegacyFormImageUrl,
  toDownloadFormMediaUrl
} from '../../helpers/form/formMedia';
import { getFormThemeColors } from '../../helpers/form/formTheme';
import './FormRendererV2.scss';

interface Props {
  definition: FormDefinition;
  answers: FormAnswerMap;
  errors?: FormErrorMap;
  disabled?: boolean;
  onChange: (answers: FormAnswerMap) => void;
  resolveMediaUrl?: (mediaId: string) => string;
  onSubmitRequested?: (completionMessage?: string) => void;
  uploadFiles?: (files: File[], element: FormElement) => Promise<FormMediaRef[]>;
}

const getValue = (answers: FormAnswerMap, id: string): any => answers[id]?.value ?? '';

const isDirectMediaUrl = (value: string): boolean => /^(https?:|data:|blob:|\/)/.test(value);

const contentText = (_element: FormElement, fallback: string): string => fallback || '';

const getRatingBounds = (element: FormElement): { min: number; max: number } => {
  const rawMin = Number(element.config?.min ?? 1);
  const rawMax = Number(element.config?.max ?? 5);
  const min = Number.isFinite(rawMin) ? Math.max(1, Math.floor(rawMin)) : 1;
  const max = Number.isFinite(rawMax) ? Math.max(min, Math.floor(rawMax)) : Math.max(min, 5);
  return { min, max };
};
const contentStyle = (element: FormElement): React.CSSProperties => ({
  color: element.config?.textColor || undefined,
  backgroundColor: element.config?.backgroundColor || undefined,
  textAlign: element.config?.textAlign || undefined,
  fontSize:
    element.config?.textSize === 'large' ? '1.15em' : element.config?.textSize === 'small' ? '0.92em' : undefined,
  padding: element.config?.backgroundColor ? '10px 12px' : undefined,
  borderRadius: element.config?.backgroundColor ? 10 : undefined
});

export const FormRendererV2: React.FC<Props> = ({
  definition,
  answers,
  errors = {},
  disabled = false,
  onChange,
  resolveMediaUrl,
  onSubmitRequested,
  uploadFiles
}) => {
  const pages = React.useMemo(() => buildFormPages(definition), [definition]);
  const flowEnabled = hasFormFlow(definition);
  const [currentPageId, setCurrentPageId] = React.useState<string>(FORM_START_PAGE_ID);
  const [pageHistory, setPageHistory] = React.useState<string[]>([]);
  const [flowErrors, setFlowErrors] = React.useState<FormErrorMap>({});
  const [terminalMessage, setTerminalMessage] = React.useState('');
  const [uploadingFieldId, setUploadingFieldId] = React.useState('');
  const [uploadErrors, setUploadErrors] = React.useState<FormErrorMap>({});
  const rendererRef = React.useRef<HTMLDivElement>(null);
  const pageHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const currentPage = pages.find(page => page.id === currentPageId) || pages[0];
  const currentPageIndex = pages.findIndex(page => page.id === currentPage.id);
  const currentPageElementIds = new Set(currentPage.elements.map(element => element.id));
  const currentPageErrors = flowEnabled
    ? validateFormDefinition(definition, answers, currentPageElementIds)
    : {};
  const isCurrentPageInvalid = Object.keys(currentPageErrors).length > 0;
  const renderedErrors = { ...errors, ...flowErrors, ...currentPageErrors, ...uploadErrors };
  const themeColors = getFormThemeColors(definition.theme);
  const themeStyle = {
    '--form-accent-color': themeColors.accentColor,
    '--form-page-background-color': themeColors.pageBackgroundColor
  } as React.CSSProperties;

  React.useEffect(() => {
    if (pages.some(page => page.id === currentPageId)) return;
    setCurrentPageId(FORM_START_PAGE_ID);
    setPageHistory([]);
    setTerminalMessage('');
  }, [pages, currentPageId]);

  React.useEffect(() => {
    if (!flowEnabled) return;
    pageHeadingRef.current?.focus();
  }, [currentPageId, terminalMessage, flowEnabled]);

  const updateAnswer = (fieldId: string, value: unknown, otherValue?: string) => {
    if (flowErrors[fieldId]) {
      setFlowErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
    onChange({
      ...answers,
      [fieldId]: {
        fieldId,
        value,
        ...(typeof otherValue !== 'undefined' ? { otherValue } : {})
      }
    });
  };

  const resolveElementMediaUrl = (element: FormElement): string => {
    const mediaUrl = normalizeFormMediaUrl(String(element.config?.mediaUrl || ''));
    if (mediaUrl) return element.type === 'IMAGE' ? resolveLegacyFormImageUrl(mediaUrl) : mediaUrl;
    const signedUrl = buildFormMediaUrl(
      String(element.config?.mediaKey || element.config?.mediaId || ''),
      element.config?.mediaToken
    );
    if (signedUrl) return signedUrl;
    const mediaId = normalizeFormMediaUrl(String(element.config?.mediaId || element.config?.mediaKey || ''));
    if (!mediaId) return '';
    if (resolveMediaUrl && !isDirectMediaUrl(mediaId)) return normalizeFormMediaUrl(resolveMediaUrl(mediaId));
    if (element.type === 'IMAGE') return resolveLegacyFormImageUrl(mediaId);
    return isDirectMediaUrl(mediaId) ? mediaId : '';
  };

  const renderChoiceOptions = (element: FormElement, multiple: boolean) => {
    const options = element.config?.options || [];
    const current = multiple
      ? Array.isArray(getValue(answers, element.id))
        ? getValue(answers, element.id)
        : []
      : getValue(answers, element.id);
    const hasOther = multiple ? current.includes('__other__') : current === '__other__';

    return (
      <div
        className="FormRendererV2__choices"
        id={`form_element_${element.id}`}
        role="group"
        aria-labelledby={`form_element_${element.id}_label`}
        aria-describedby={renderedErrors[element.id] ? `form_element_${element.id}_error` : undefined}
        aria-invalid={!!renderedErrors[element.id]}
        tabIndex={-1}
      >
        {options.map((option: FormOption) => {
          const checked = multiple ? current.includes(option.value) : current === option.value;
          return (
            <label className="FormRendererV2__choice" key={option.id}>
              <input
                aria-label={option.label}
                type={multiple ? 'checkbox' : 'radio'}
                name={element.id}
                checked={checked}
                disabled={disabled}
                onChange={() => {
                  if (!multiple) {
                    updateAnswer(element.id, option.value);
                    return;
                  }
                  const next = checked ? current.filter(value => value !== option.value) : [...current, option.value];
                  updateAnswer(element.id, next, answers[element.id]?.otherValue);
                }}
              />
              <span>{option.label}</span>
            </label>
          );
        })}

        {element.config?.allowOther && (
          <>
            <label className="FormRendererV2__choice">
              <input
                aria-label={element.config.otherLabel || 'Other'}
                type={multiple ? 'checkbox' : 'radio'}
                name={element.id}
                checked={hasOther}
                disabled={disabled}
                onChange={() => {
                  if (!multiple) {
                    updateAnswer(element.id, '__other__', answers[element.id]?.otherValue || '');
                    return;
                  }
                  const next = hasOther ? current.filter(value => value !== '__other__') : [...current, '__other__'];
                  updateAnswer(element.id, next, answers[element.id]?.otherValue || '');
                }}
              />
              <span>{element.config.otherLabel || 'Other'}</span>
            </label>
            {hasOther && (
              <input
                type="text"
                className="form-control FormRendererV2__control FormRendererV2__other"
                value={answers[element.id]?.otherValue || ''}
                disabled={disabled}
                placeholder={`Enter ${element.config.otherLabel || 'other'} answer`}
                aria-label={`${element.config.otherLabel || 'Other'} details`}
                onChange={event => updateAnswer(element.id, current, event.target.value)}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderQuestion = (element: FormElement) => {
    const value = getValue(answers, element.id);
    const common = {
      id: `form_element_${element.id}`,
      disabled,
      className: `form-control FormRendererV2__control${renderedErrors[element.id] ? ' is-invalid' : ''}`,
      'aria-labelledby': `form_element_${element.id}_label`,
      'aria-describedby':
        [
          element.description ? `form_element_${element.id}_description` : '',
          renderedErrors[element.id] ? `form_element_${element.id}_error` : ''
        ]
          .filter(Boolean)
          .join(' ') || undefined,
      'aria-invalid': !!renderedErrors[element.id]
    };

    switch (element.type) {
      case 'SHORT_TEXT':
      case 'EMAIL':
      case 'PHONE':
      case 'URL':
        return (
          <input
            {...common}
            type={
              element.type === 'EMAIL'
                ? 'email'
                : element.type === 'URL'
                ? 'url'
                : element.type === 'PHONE'
                ? 'tel'
                : 'text'
            }
            value={value}
            placeholder={element.config?.placeholder || ''}
            maxLength={element.config?.maxLength}
            onChange={event => updateAnswer(element.id, event.target.value)}
          />
        );
      case 'LONG_TEXT':
        return (
          <textarea
            {...common}
            rows={4}
            value={value}
            placeholder={element.config?.placeholder || ''}
            maxLength={element.config?.maxLength}
            onChange={event => updateAnswer(element.id, event.target.value)}
          />
        );
      case 'NUMBER':
        return (
          <input
            {...common}
            type="number"
            value={value}
            min={element.config?.min}
            max={element.config?.max}
            step={element.config?.step}
            onChange={event => updateAnswer(element.id, event.target.value)}
          />
        );
      case 'RATING': {
        const bounds = getRatingBounds(element);
        const numericValue = Number(value);
        return (
          <div
            className={`FormRendererV2__rating${renderedErrors[element.id] ? ' is-invalid' : ''}`}
            id={`form_element_${element.id}`}
            role="radiogroup"
            aria-labelledby={`form_element_${element.id}_label`}
            aria-describedby={renderedErrors[element.id] ? `form_element_${element.id}_error` : undefined}
            aria-invalid={!!renderedErrors[element.id]}
            tabIndex={-1}
          >
            {Array.from({ length: bounds.max }, (_, index) => index + 1).map(score => {
              const belowMin = score < bounds.min;
              const checked = numericValue === score;
              const filled = Number.isFinite(numericValue) && numericValue >= score && !belowMin;
              return (
                <button
                  key={score}
                  type="button"
                  className={`FormRendererV2__ratingStar${filled ? ' FormRendererV2__ratingStar--filled' : ''}${
                    checked ? ' FormRendererV2__ratingStar--checked' : ''
                  }`}
                  disabled={disabled || belowMin}
                  role="radio"
                  aria-checked={checked}
                  aria-label={`${score} star${score === 1 ? '' : 's'}`}
                  onClick={() => updateAnswer(element.id, score)}
                >
                  ★
                </button>
              );
            })}
          </div>
        );
      }
      case 'DATE':
      case 'DATE_TIME':
        return (
          <input
            {...common}
            type={element.type === 'DATE' ? 'date' : 'datetime-local'}
            value={value}
            onChange={event => updateAnswer(element.id, event.target.value)}
          />
        );
      case 'YES_NO':
        return (
          <div
            className="FormRendererV2__choices FormRendererV2__choices--inline"
            id={`form_element_${element.id}`}
            role="group"
            aria-labelledby={`form_element_${element.id}_label`}
            aria-describedby={renderedErrors[element.id] ? `form_element_${element.id}_error` : undefined}
            aria-invalid={!!renderedErrors[element.id]}
            tabIndex={-1}
          >
            {[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ].map(option => (
              <label className="FormRendererV2__choice" key={String(option.value)}>
                <input
                  aria-label={option.label}
                  type="radio"
                  name={element.id}
                  checked={value === option.value}
                  disabled={disabled}
                  onChange={() => updateAnswer(element.id, option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        );
      case 'SINGLE_CHOICE':
        return renderChoiceOptions(element, false);
      case 'MULTIPLE_CHOICE':
        return renderChoiceOptions(element, true);
      case 'ACKNOWLEDGEMENT':
        return (
          <label className="FormRendererV2__choice">
            <input
              id={`form_element_${element.id}`}
              type="checkbox"
              checked={value === true}
              disabled={disabled}
              aria-labelledby={`form_element_${element.id}_label`}
              aria-describedby={renderedErrors[element.id] ? `form_element_${element.id}_error` : undefined}
              aria-invalid={!!renderedErrors[element.id]}
              onChange={event => updateAnswer(element.id, event.target.checked)}
            />
            <span>{element.description || 'I acknowledge this information'}</span>
          </label>
        );
      case 'FILE_UPLOAD': {
        const files = Array.isArray(value) ? (value as FormMediaRef[]) : [];
        const persistedFiles = files.filter(file => !String(file.id).startsWith('__uploading__'));
        const isUploading = uploadingFieldId === element.id;
        return (
          <div>
            <div className="alert alert-light border mb-2">
              File responses are not currently accepted for this form.
            </div>
            <input
              {...common}
              type="file"
              hidden
              multiple={(element.config?.maxFiles || 1) > 1}
              accept={(element.config?.allowedMimeTypes || []).join(',') || undefined}
              disabled={disabled || isUploading || !uploadFiles}
              onChange={async event => {
                const selectedFiles = Array.from(event.target.files || []);
                event.target.value = '';
                if (!selectedFiles.length || !uploadFiles) return;
                const maxFiles = element.config?.maxFiles || 1;
                if (persistedFiles.length + selectedFiles.length > maxFiles) {
                  setUploadErrors(prev => ({
                    ...prev,
                    [element.id]: `Please upload no more than ${maxFiles} file(s).`
                  }));
                  return;
                }
                const uploadingRef: FormMediaRef = {
                  id: `__uploading__${Date.now()}`,
                  name: 'Uploading…',
                  size: -1,
                  mimeType: '',
                  url: ''
                };
                setUploadingFieldId(element.id);
                setUploadErrors(prev => ({ ...prev, [element.id]: '' }));
                updateAnswer(element.id, [...persistedFiles, uploadingRef]);
                try {
                  const uploaded = await uploadFiles(selectedFiles, element);
                  updateAnswer(element.id, [...persistedFiles, ...uploaded]);
                } catch (error) {
                  updateAnswer(element.id, persistedFiles);
                  setUploadErrors(prev => ({
                    ...prev,
                    [element.id]: error instanceof Error ? error.message : 'Unable to upload files'
                  }));
                } finally {
                  setUploadingFieldId('');
                }
              }}
            />
            {isUploading && (
              <div className="small text-info mt-1" role="status" aria-live="polite">
                Uploading…
              </div>
            )}
            {persistedFiles.map(file => (
              <div className="d-flex align-items-center justify-content-between mt-2" key={file.id}>
                <a
                  href={toDownloadFormMediaUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={file.name}
                >
                  {file.name}
                </a>
                {!disabled && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      updateAnswer(
                        element.id,
                        persistedFiles.filter(item => item.id !== file.id)
                      )
                    }
                    aria-label={`Remove ${file.name}`}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {uploadErrors[element.id] && <div className="invalid-feedback d-block">{uploadErrors[element.id]}</div>}
          </div>
        );
      }
      default:
        return <div className="text-muted">Unsupported field type: {element.type}</div>;
    }
  };

  const renderElement = (element: FormElement) => {
    if (!isElementVisible(element, answers)) return null;

    if (element.kind !== 'QUESTION') {
      switch (element.type) {
        case 'HEADING':
        case 'SECTION':
          return (
            <h4 className="FormRendererV2__heading" style={contentStyle(element)}>
              {contentText(element, element.title || '')}
            </h4>
          );
        case 'PARAGRAPH':
          return (
            <p className="FormRendererV2__paragraph" style={contentStyle(element)}>
              {contentText(element, element.description || (element.title === 'Paragraph' ? '' : element.title) || '')}
            </p>
          );
        case 'URL': {
          const href = String(element.config?.linkUrl || '').trim();
          if (!href) return null;
          return (
            <div className="FormRendererV2__linkBlock" style={contentStyle(element)}>
              <a href={href} target="_blank" rel="noopener noreferrer">
                {contentText(element, element.title || href)}
              </a>
              {element.description && <div className="FormRendererV2__linkDescription">{element.description}</div>}
            </div>
          );
        }
        case 'IMAGE': {
          const src = resolveElementMediaUrl(element);
          return src ? (
            <img className="FormRendererV2__image" src={src} alt={element.title || ''} loading="lazy" />
          ) : null;
        }
        case 'ATTACHMENT': {
          const href = resolveElementMediaUrl(element);
          if (!href) return null;
          const fileName = element.config?.mediaName || element.title || 'Download file';
          const downloadHref = toDownloadFormMediaUrl(href);
          return (
            <div className="FormRendererV2__attachment">
              <div className="FormRendererV2__attachmentInfo">
                <div className="FormRendererV2__attachmentName">{element.title || fileName}</div>
                {element.description && (
                  <div className="FormRendererV2__attachmentDescription">{element.description}</div>
                )}
                <div className="FormRendererV2__attachmentMeta">
                  {fileName}
                  {element.config?.mediaSize ? ` · ${Math.ceil(element.config.mediaSize / 1024)} KB` : ''}
                </div>
              </div>
              <div className="FormRendererV2__attachmentActions">
                <a className="btn btn-sm btn-outline-primary" href={href} target="_blank" rel="noopener noreferrer">
                  Open
                </a>
                <a
                  className="btn btn-sm btn-primary"
                  href={downloadHref}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </a>
              </div>
            </div>
          );
        }
        case 'DIVIDER':
        case 'PAGE_BREAK':
          return <hr className="FormRendererV2__divider" />;
        default:
          return null;
      }
    }

    return (
      <div className="FormRendererV2__group" data-form-element-id={element.id} tabIndex={-1}>
        <div className="FormRendererV2__label" id={`form_element_${element.id}_label`}>
          {element.title}
          {isFormElementRequired(element) && <span className="FormRendererV2__required">*</span>}
        </div>
        {element.description && element.type !== 'ACKNOWLEDGEMENT' && (
          <div className="FormRendererV2__description" id={`form_element_${element.id}_description`}>
            {element.description}
          </div>
        )}
        {renderQuestion(element)}
        {renderedErrors[element.id] && (
          <div className="invalid-feedback d-block" id={`form_element_${element.id}_error`} role="alert">
            {renderedErrors[element.id]}
          </div>
        )}
      </div>
    );
  };

  const goBack = () => {
    const previousPageId = pageHistory[pageHistory.length - 1];
    if (!previousPageId) return;
    setCurrentPageId(previousPageId);
    setPageHistory(history => history.slice(0, -1));
    setFlowErrors({});
    setTerminalMessage('');
  };

  const goNext = () => {
    const pageElementIds = new Set(currentPage.elements.map(element => element.id));
    const nextErrors = validateFormDefinition(definition, answers, pageElementIds);
    if (Object.keys(nextErrors).length > 0) {
      setFlowErrors(nextErrors);
      return;
    }
    setFlowErrors({});

    const action = resolveNextPageAction(definition, currentPage.id, answers);
    if (action?.type === 'GO_TO_PAGE') {
      setPageHistory(history => [...history, currentPage.id]);
      setCurrentPageId(action.targetId);
      return;
    }
    if (action?.type === 'SUBMIT') {
      onSubmitRequested?.();
      return;
    }
    if (action?.type === 'END_WITH_MESSAGE') {
      setTerminalMessage(action.message);
      return;
    }

    const nextPage = pages[currentPageIndex + 1];
    if (nextPage) {
      setPageHistory(history => [...history, currentPage.id]);
      setCurrentPageId(nextPage.id);
      return;
    }
    onSubmitRequested?.();
  };

  if (terminalMessage) {
    return (
      <div className="FormRendererV2" style={themeStyle} ref={rendererRef}>
        <div className="FormRendererV2__terminal">
          <h4 ref={pageHeadingRef} tabIndex={-1}>
            Form complete
          </h4>
          <p>{terminalMessage}</p>
        </div>
        <div className="FormRendererV2__navigation">
          {pageHistory.length > 0 && (
            <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => onSubmitRequested?.(terminalMessage)}>
            Submit response
          </button>
        </div>
      </div>
    );
  }

  const elementsToRender = flowEnabled ? currentPage.elements : definition.elements;

  return (
    <div className="FormRendererV2" style={themeStyle} ref={rendererRef}>
      {flowEnabled && (
        <div className="FormRendererV2__pageHeader">
          <div>
            <h4 ref={pageHeadingRef} tabIndex={-1}>
              {currentPage.title}
            </h4>
            {currentPage.description && <p>{currentPage.description}</p>}
          </div>
          {pages.length > 1 && (
            <span>
              Page {currentPageIndex + 1} of {pages.length}
            </span>
          )}
        </div>
      )}

      {elementsToRender.map(element => (
        <React.Fragment key={element.id}>{renderElement(element)}</React.Fragment>
      ))}

      {flowEnabled && (
        <div className="FormRendererV2__navigationWrap">
          {isCurrentPageInvalid && (
            <div
              className="FormRendererV2__requiredNotice"
              role="alert"
              style={{ padding: '9px 12px', border: '1px solid #f1c7c7', background: '#fff5f5', color: '#a52a2a' }}
            >
              Please complete all required fields before continuing.
            </div>
          )}
          <div className="FormRendererV2__navigation">
            {pageHistory.length > 0 && (
              <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
                Back
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={goNext}
              disabled={!!uploadingFieldId || isCurrentPageInvalid}
            >
              {currentPageIndex === pages.length - 1 ? 'Submit response' : 'Next'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
